// src/stores/auth.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { app, projectFirestore } from "@/firebase/config";
import { sendLoginNotification } from "@/composables/useTelegram";
import {
  storeActiveSession,
  revokeSession,
} from "@/composables/useSessionManager";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(null);
  const userData = ref(null);
  const isAuthenticated = ref(false);
  const loading = ref(false);
  const error = ref(null);

  // Getters
  const userId = computed(() => user.value?.uid || null);
  const userEmail = computed(() => user.value?.email || null);
  const isLoggedIn = computed(
    () => isAuthenticated.value && user.value !== null
  );

  // Initialize Firebase Auth
  const auth = getAuth(app);

  // Initialize from localStorage
  const initializeFromStorage = () => {
    const storedUser = localStorage.getItem("user");
    const storedUserData = localStorage.getItem("userData");

    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
        isAuthenticated.value = true;
      } catch (e) {
        console.error("Error parsing stored user:", e);
        localStorage.removeItem("user");
      }
    }

    if (storedUserData) {
      try {
        userData.value = JSON.parse(storedUserData);
      } catch (e) {
        console.error("Error parsing stored userData:", e);
        localStorage.removeItem("userData");
      }
    }
  };

  // Set up auth state listener
  const setupAuthListener = () => {
    onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        clearUser();
      }
    });
  };

  // Helper function to check if input is email
  const isEmail = (input) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input);
  };

  // Helper function to get email from username
  const getEmailFromUsername = async (username) => {
    try {
      const usersRef = collection(projectFirestore, "users");
      const q = query(usersRef, where("username", "==", username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return userDoc.data().email;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user by username:", error);
      return null;
    }
  };

  // Actions
  const setUser = (newUser) => {
    user.value = newUser;
    isAuthenticated.value = true;
    localStorage.setItem("user", JSON.stringify(newUser));
  };

  const setUserData = (newUserData) => {
    userData.value = newUserData;
    localStorage.setItem("userData", JSON.stringify(newUserData));
  };

  const clearUser = () => {
    user.value = null;
    userData.value = null;
    isAuthenticated.value = false;
    error.value = null;
    localStorage.removeItem("user");
    localStorage.removeItem("userData");
  };

  const signIn = async (emailOrUsername, password) => {
    loading.value = true;
    error.value = null;

    try {
      let email = emailOrUsername;

      // If input is not an email, try to get email from username
      if (!isEmail(emailOrUsername)) {
        const emailFromUsername = await getEmailFromUsername(emailOrUsername);
        if (!emailFromUsername) {
          throw new Error("auth/user-not-found");
        }
        email = emailFromUsername;
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setUser(userCredential.user);

      // Generate session token and store active session
      const sessionToken = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const deviceInfo = {
        browser: navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
          ? "Firefox"
          : navigator.userAgent.includes("Safari")
          ? "Safari"
          : "Unknown",
        os: navigator.platform,
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        ip: "0.0.0.0", // This would need to be fetched from an IP service
      };

      // Store active session
      try {
        await storeActiveSession(userCredential.user, deviceInfo, sessionToken);
      } catch (sessionError) {
        console.warn("Failed to store session:", sessionError);
      }

      // Send login notification to Telegram (don't wait for it to complete)
      try {
        sendLoginNotification(userCredential.user).catch((error) => {
          console.warn("Failed to send Telegram notification:", error);
        });
      } catch (error) {
        console.warn("Error initiating Telegram notification:", error);
      }

      return { success: true, user: userCredential.user };
    } catch (err) {
      error.value = err.message;
      clearUser();
      return { success: false, error: err.message };
    } finally {
      loading.value = false;
    }
  };

  const signOutUser = async () => {
    loading.value = true;
    error.value = null;

    try {
      // Revoke current session if exists
      const sessionId = localStorage.getItem("sessionId");
      if (sessionId) {
        try {
          await revokeSession(sessionId);
          localStorage.removeItem("sessionId");
        } catch (sessionError) {
          console.warn("Failed to revoke session:", sessionError);
        }
      }

      await signOut(auth);
      clearUser();
      return { success: true };
    } catch (err) {
      error.value = err.message;
      return { success: false, error: err.message };
    } finally {
      loading.value = false;
    }
  };

  const updateUserData = (newData) => {
    userData.value = { ...userData.value, ...newData };
    localStorage.setItem("userData", JSON.stringify(userData.value));
  };

  const clearError = () => {
    error.value = null;
  };

  // Initialize the store
  initializeFromStorage();
  setupAuthListener();

  return {
    // State
    user,
    userData,
    isAuthenticated,
    loading,
    error,

    // Getters
    userId,
    userEmail,
    isLoggedIn,

    // Actions
    signIn,
    signOutUser,
    setUser,
    setUserData,
    clearUser,
    updateUserData,
    clearError,
  };
});
